import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
} from "../../../Components/Shared/ImageSeoFields";

function CorporateInteriorDesignCategoryModal({
    modalOpen,
    toggleModal,
    category,
    handleSaveCategory,
}) {
    const [categoryData, setCategoryData] = useState({
        name: "",
        categoryName_ur: "",
        status: true,
        image: null,
        image_alt: "",
        image_title: "",
        description: "",
        description_ur: "",
        custom_url: ""
    });

    const [nameError, setNameError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [imageError, setImageError] = useState("");
    const [customUrlError, setCustomUrlError] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");

    useEffect(() => {
        if (category) {
            setCategoryData({
                name: category.name || "",
                categoryName_ur: category.categoryName_ur || "",
                status: category.status !== undefined ? category.status : true,
                image: category.image || null,
                image_alt: category.image_alt || "",
                image_title: category.image_title || "",
                description: category.description || "",
                description_ur: category.description_ur || "",
                custom_url: category.custom_url || ""
            });
            setSelectedImage(null);
            const previewUrl = category.image_url || category.image || "";
            setImagePreview(previewUrl);
        } else {
            setCategoryData({
                name: "",
                categoryName_ur: "",
                status: true,
                image: null,
                image_alt: "",
                image_title: "",
                description: "",
                description_ur: "",
                custom_url: ""
            });
            setSelectedImage(null);
            setImagePreview("");
        }
        setNameError("");
        setDescriptionError("");
        setImageError("");
        setCustomUrlError("");
    }, [category, modalOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCategoryData((prevData) => ({
            ...prevData,
            [name]: value === "true" ? true : value === "false" ? false : value,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                Swal.fire("Error!", "Please select a valid image file.", "error");
                e.target.value = '';
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                Swal.fire("Error!", "Image size should be less than 5MB.", "error");
                e.target.value = '';
                return;
            }

            const img = new Image();
            img.onload = () => {
                if (img.width !== 1200 || img.height !== 900) {
                    Swal.fire("Error!", "Image resolution must be exactly 1200x900 pixels.", "error");
                    e.target.value = '';
                    return;
                }

                setSelectedImage(file);
                setCategoryData(prev => ({ ...prev, image: file }));
                const preview = URL.createObjectURL(file);
                setImagePreview(preview);
            };
            img.onerror = () => {
                Swal.fire("Error!", "Failed to load image. Please try again.", "error");
                e.target.value = '';
            };
            img.src = URL.createObjectURL(file);
        }
    };

    const removeImage = () => {
        if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
        }
        setSelectedImage(null);
        setCategoryData(prev => ({ ...prev, image: null }));
        setImagePreview("");

        const fileInput = document.getElementById('interior-category-image');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setNameError("");
        setDescriptionError("");
        setImageError("");
        setCustomUrlError("");

        let hasError = false;

        if (!categoryData.name.trim()) {
            setNameError("Category name is required.");
            hasError = true;
        }

        if (!categoryData.custom_url.trim()) {
            setCustomUrlError("Custom URL is required.");
            hasError = true;
        }

        if (!categoryData.description.trim()) {
            setDescriptionError("Description is required.");
            hasError = true;
        }

        if (!category && !selectedImage && !imagePreview) {
            setImageError("Image is required.");
            hasError = true;
        }

        if (category && !selectedImage && !imagePreview && !category.image_url && !category.image) {
            setImageError("Image is required.");
            hasError = true;
        }

        if (hasError) return;

        const formData = new FormData();
        formData.append('name', categoryData.name);
        if (categoryData.categoryName_ur) formData.append('categoryName_ur', categoryData.categoryName_ur);
        formData.append('description', categoryData.description);
        if (categoryData.description_ur) formData.append('description_ur', categoryData.description_ur);
        formData.append('status', categoryData.status);
        formData.append('custom_url', categoryData.custom_url);

        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        appendImageSeoToFormData(formData, categoryData.image_alt, categoryData.image_title);

        handleSaveCategory(formData, category ? category.id : null);

        setCategoryData({
            name: "",
            categoryName_ur: "",
            status: true,
            image: null,
            image_alt: "",
            image_title: "",
            description: "",
            description_ur: "",
            custom_url: ""
        });
        setSelectedImage(null);
        setImagePreview("");
        toggleModal();
    };

    const handleCloseModal = () => {
        if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
        }
        setCategoryData({
            name: "",
            categoryName_ur: "",
            status: true,
            image: null,
            image_alt: "",
            image_title: "",
            description: "",
            description_ur: "",
            custom_url: ""
        });
        setSelectedImage(null);
        setImagePreview("");
        setNameError("");
        setDescriptionError("");
        setImageError("");
        setCustomUrlError("");
        toggleModal();
    };

    return (
        modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white p-8 rounded-3xl w-[600px] max-h-[90vh] overflow-y-auto relative">
                    <XMarkIcon
                        className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer"
                        onClick={handleCloseModal}
                    />
                    <h3 className="text-2xl font-semibold mb-6 text-center">
                        {category ? "Edit Corporate Interior Design Category" : "Add Corporate Interior Design Category"}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={categoryData.name}
                                onChange={handleInputChange}
                                placeholder="Enter category name"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {nameError && (
                                <p className="text-red-500 text-sm mt-1">{nameError}</p>
                            )}
                            <UrduInput
                                label="زمرہ کا نام"
                                name="categoryName_ur"
                                value={categoryData.categoryName_ur}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Custom URL *
                            </label>
                            <input
                                type="text"
                                name="custom_url"
                                value={categoryData.custom_url}
                                onChange={handleInputChange}
                                placeholder="Enter custom URL"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {customUrlError && (
                                <p className="text-red-500 text-sm mt-1">{customUrlError}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description *
                            </label>
                            <textarea
                                name="description"
                                value={categoryData.description}
                                onChange={handleInputChange}
                                placeholder="Enter category description"
                                rows="3"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {descriptionError && (
                                <p className="text-red-500 text-sm mt-1">{descriptionError}</p>
                            )}
                            <UrduInput
                                label="تفصیل"
                                name="description_ur"
                                value={categoryData.description_ur}
                                onChange={handleInputChange}
                                type="textarea"
                                rows={3}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                                Category Image {(!category || (!category.image_url && !category.image)) && "*"}
                            </label>
                            <div
                                className={`border border-dashed p-4 rounded-md flex items-center justify-center min-h-[220px] ${
                                    !selectedImage && !imagePreview
                                        ? "border-red-300 bg-red-50"
                                        : "border-gray-300"
                                }`}
                            >
                                {imagePreview ? (
                                    <div className="relative group space-y-3 w-full flex flex-col items-center">
                                        <div className="w-full max-w-md aspect-[4/3] max-h-[320px] bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center mx-auto">
                                            <img
                                                src={imagePreview}
                                                alt="Category preview"
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 text-center">
                                            Preview · 1200 × 900 (4:3)
                                        </p>
                                        <div className="flex justify-center items-center gap-2">
                                            <input
                                                type="file"
                                                id="interior-category-image"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                            <label
                                                htmlFor="interior-category-image"
                                                className="btn btn-view text-white justify-center cursor-pointer"
                                            >
                                                Change Image
                                            </label>
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="btn btn-delete text-white justify-center"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full flex items-center justify-center">
                                        <input
                                            type="file"
                                            id="interior-category-image"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageChange}
                                        />
                                        <label
                                            htmlFor="interior-category-image"
                                            className="flex flex-col items-center justify-center text-center cursor-pointer w-full py-8"
                                        >
                                            <div className="text-gray-500 mb-2 flex justify-center">
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            </div>
                                            <span className="text-sm text-gray-500">
                                                Click to upload image
                                            </span>
                                            <span className="text-xs text-red-400 mt-1 font-medium">
                                                * Required - PNG, JPG, GIF up to 5MB
                                            </span>
                                            <span className="text-xs text-blue-600 mt-1 font-medium">
                                                Resolution must be exactly 1200x900 pixels
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                            {imageError && (
                                <p className="text-red-500 text-sm mt-1">{imageError}</p>
                            )}
                            {!selectedImage && !imagePreview && (!category || (!category.image_url && !category.image)) && (
                                <p className="text-red-500 text-xs mt-1">Category image is required</p>
                            )}
                            <ImageSeoFields
                                imageAlt={categoryData.image_alt}
                                imageTitle={categoryData.image_title}
                                onAltChange={(value) =>
                                    setCategoryData((prev) => ({ ...prev, image_alt: value }))
                                }
                                onTitleChange={(value) =>
                                    setCategoryData((prev) => ({ ...prev, image_title: value }))
                                }
                                className="mt-3"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="btn btn-reset-filter justify-center"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`btn justify-center ${
                                    category ? "btn-edit text-white" : "btn-primary"
                                }`}
                            >
                                {category ? "Update" : "Save"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    );
}

export default CorporateInteriorDesignCategoryModal;
